import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur, pct, dateIt } from "@/lib/format";
import { PrivacyBadge, QuoteStatusBadge, PageHeader } from "@/components/shared";
import { DECISION, OPERATION_STATUS_CLS } from "@/lib/constants";
import { PlusButton, ManualItem, EntryDialog } from "@/components/DashboardEntries";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Users, Building2, FileText, AlertTriangle, CalendarClock, TrendingUp, Calculator, Settings, Workflow, Clock } from "lucide-react";

const KPI = [
  { key: "new_contacts", label: "Nuovi Contatti", icon: Users, color: "text-blue-600 bg-blue-50", to: "/contatti" },
  { key: "active_opportunities", label: "Opportunità Attive", icon: Building2, color: "text-emerald-600 bg-emerald-50", to: "/immobili" },
  { key: "quotes_awaiting", label: "Preventivi in Attesa", icon: FileText, color: "text-violet-600 bg-violet-50", to: "/preventivi" },
  { key: "overdue_followups", label: "Follow-up in Ritardo", icon: AlertTriangle, color: "text-rose-600 bg-rose-50", to: "/followup" },
  { key: "upcoming_actions", label: "Azioni in Arrivo", icon: CalendarClock, color: "text-amber-600 bg-amber-50", to: "/followup" },
];

const EMPTY_DIALOG = { open: false, section: null, label: "", entry: null };

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(EMPTY_DIALOG);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const load = useCallback(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = (section, label, entry = null) => setDialog({ open: true, section, label, entry });
  const closeDialog = () => setDialog(EMPTY_DIALOG);
  const manualFor = (sec) => (data && data.manual_entries && data.manual_entries[sec]) || [];

  const onEdit = (entry) => {
    const sec = entry.section;
    const label = sec.startsWith("ops:") ? sec.slice(4) : (SECTION_LABELS[sec] || sec);
    openAdd(sec, label, entry);
  };
  const onDelete = async (entry) => {
    if (!window.confirm(`Eliminare la voce manuale "${entry.title}"?`)) return;
    try { await api.delete(`/dashboard-entries/${entry.id}`); toast.success("Voce eliminata"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (!data) {
    return (
      <div className="space-y-6">
        <PageHeader title="Pannello di Controllo" subtitle="Panoramica operativa InvestArt" />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  const opManual = Object.entries(data.manual_entries || {})
    .filter(([k]) => k.startsWith("ops:"))
    .flatMap(([, v]) => v);

  const renderManual = (sec) => manualFor(sec).map((e) => (
    <ManualItem key={e.id} entry={e} onEdit={onEdit} onDelete={onDelete} />
  ));

  return (
    <div className="space-y-6" data-testid="dashboard">
      <PageHeader title="Pannello di Controllo" subtitle="Panoramica operativa · Reggio Emilia">
        <Button variant="outline" onClick={() => navigate("/valutazione")} data-testid="quick-evaluation">
          <Calculator className="h-4 w-4 mr-2" />Valutazione
        </Button>
        {isAdmin && (
          <Button variant="outline" onClick={() => navigate("/impostazioni")} data-testid="quick-settings">
            <Settings className="h-4 w-4 mr-2" />Impostazioni
          </Button>
        )}
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {KPI.map((k) => (
          <Card key={k.key} onClick={() => navigate(k.to)} className="p-5 cursor-pointer hover:-translate-y-0.5 hover:shadow-md transition-all" data-testid={`kpi-${k.key}`}>
            <div className={`h-10 w-10 rounded-lg grid place-items-center ${k.color}`}><k.icon className="h-5 w-5" /></div>
            <div className="mt-3 font-display text-3xl font-bold text-slate-900">{data.kpis[k.key]}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{k.label}</div>
          </Card>
        ))}
      </div>

      {/* Opportunity buckets */}
      <div>
        <h2 className="font-display text-xl font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" /> Opportunità per Decisione
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          {["BUY", "NEGOTIATE", "DO_NOT_BUY"].map((b) => (
            <Card key={b} className={`p-4 border-l-4 ${DECISION[b].border}`} data-testid={`bucket-${b}`}>
              <div className="flex items-center justify-between mb-3">
                <span className={`inline-flex rounded-md px-2.5 py-1 text-xs font-bold ${DECISION[b].cls}`}>{DECISION[b].label}</span>
                <span className="font-display text-2xl font-bold text-slate-800">{data.opportunity_buckets[b].length}</span>
              </div>
              <div className="space-y-2">
                {data.opportunity_buckets[b].length === 0 && <div className="text-xs text-muted-foreground py-2">Nessuna opportunità.</div>}
                {data.opportunity_buckets[b].map((o) => (
                  <button key={o.id} onClick={() => navigate(`/immobili/${o.property_id}`)} className="w-full text-left rounded-lg border border-border bg-muted/40 px-3 py-2 hover:bg-muted transition-colors" data-testid={`bucket-item-${o.id}`}>
                    <div className="text-sm font-medium text-slate-800 truncate">{o.label}</div>
                    <div className="text-xs text-muted-foreground flex gap-3 mt-0.5"><span>Utile {eur(o.profit)}</span><span>ROI {pct(o.roi)}</span></div>
                  </button>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Operations */}
      <div data-testid="dashboard-operations">
        <h2 className="font-display text-xl font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <Workflow className="h-5 w-5 text-primary" /> Operazioni in corso ({data.operations_active})
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
          {data.operations_by_status.map((s) => (
            <div key={s.status} className="relative rounded-lg border border-border px-3 py-2 hover:bg-muted/50 transition-colors" data-testid={`ops-stat-${s.status}`}>
              <PlusButton testid={`add-ops-${s.status}`} onClick={() => openAdd(`ops:${s.status}`, s.status)} />
              <button onClick={() => navigate("/operazioni-in-corso")} className="text-left w-full pr-6">
                <div className="font-display text-2xl font-bold text-slate-800">{s.count}</div>
                <div className="mt-1"><span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${OPERATION_STATUS_CLS[s.status] || ""}`}>{s.status}</span></div>
              </button>
            </div>
          ))}
        </div>

        {opManual.length > 0 && (
          <div className="space-y-2 mb-4" data-testid="ops-manual-list">
            {opManual.map((e) => (
              <ManualItem
                key={e.id}
                entry={e}
                onEdit={onEdit}
                onDelete={onDelete}
                badge={<span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${OPERATION_STATUS_CLS[e.section.slice(4)] || ""}`}>{e.section.slice(4)}</span>}
              />
            ))}
          </div>
        )}

        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Clock className="h-4 w-4 text-amber-600" />Operazioni ferme da tempo</h3>
            <div className="flex items-center gap-2">
              <PlusButton testid="add-stalled_operations" onClick={() => openAdd("stalled_operations", "Operazioni ferme da tempo")} />
              <button onClick={() => navigate("/operazioni-in-corso")} className="text-xs text-primary font-medium hover:underline">Vedi tutte</button>
            </div>
          </div>
          <div className="space-y-2">
            {data.stalled_operations.length === 0 && manualFor("stalled_operations").length === 0 && <div className="text-sm text-muted-foreground py-4">Nessuna operazione ferma da oltre 14 giorni.</div>}
            {renderManual("stalled_operations")}
            {data.stalled_operations.map((o) => (
              <button key={o.id} onClick={() => navigate(`/operazioni/${o.id}`)} className="w-full flex items-center justify-between rounded-lg bg-amber-50/60 border border-amber-100 px-3 py-2 text-left" data-testid={`stalled-op-${o.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{o.property_label || "Operazione"}</div>
                  <div className="text-xs text-muted-foreground truncate">{o.status}{o.next_action ? ` · ${o.next_action}` : ""}</div>
                </div>
                <div className="text-xs font-semibold text-amber-700 shrink-0 ml-2">{o.days_stalled} gg</div>
              </button>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800">Follow-up in Ritardo</h3>
            <div className="flex items-center gap-2">
              <PlusButton testid="add-overdue_followups" onClick={() => openAdd("overdue_followups", "Follow-up in Ritardo")} />
              <button onClick={() => navigate("/followup")} className="text-xs text-primary font-medium hover:underline">Vedi tutti</button>
            </div>
          </div>
          <div className="space-y-2">
            {data.overdue_followups.length === 0 && manualFor("overdue_followups").length === 0 && <div className="text-sm text-muted-foreground py-4">Nessun follow-up in ritardo.</div>}
            {renderManual("overdue_followups")}
            {data.overdue_followups.map((f) => (
              <div key={f.id} className="flex items-center justify-between rounded-lg bg-rose-50/60 border border-rose-100 px-3 py-2" data-testid={`overdue-${f.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{f.linked_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{f.next_action}</div>
                </div>
                <div className="text-xs font-semibold text-rose-600 shrink-0 ml-2">{dateIt(f.next_action_date)}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800">Prossime Azioni</h3>
            <div className="flex items-center gap-2">
              <PlusButton testid="add-upcoming_actions" onClick={() => openAdd("upcoming_actions", "Prossime Azioni")} />
              <button onClick={() => navigate("/followup")} className="text-xs text-primary font-medium hover:underline">Vedi tutti</button>
            </div>
          </div>
          <div className="space-y-2">
            {data.upcoming_followups.length === 0 && manualFor("upcoming_actions").length === 0 && <div className="text-sm text-muted-foreground py-4">Nessuna azione programmata.</div>}
            {renderManual("upcoming_actions")}
            {data.upcoming_followups.map((f) => (
              <div key={f.id} className="flex items-center justify-between rounded-lg bg-muted/40 border border-border px-3 py-2" data-testid={`upcoming-${f.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{f.linked_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{f.next_action}</div>
                </div>
                <div className="text-xs font-semibold text-slate-500 shrink-0 ml-2">{dateIt(f.next_action_date)}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800">Nuovi Contatti</h3>
            <div className="flex items-center gap-2">
              <PlusButton testid="add-new_contacts" onClick={() => openAdd("new_contacts", "Nuovi Contatti")} />
              <button onClick={() => navigate("/contatti")} className="text-xs text-primary font-medium hover:underline">Vedi tutti</button>
            </div>
          </div>
          <div className="space-y-2">
            {data.new_contacts.length === 0 && manualFor("new_contacts").length === 0 && <div className="text-sm text-muted-foreground py-4">Nessun contatto recente.</div>}
            {renderManual("new_contacts")}
            {data.new_contacts.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{c.company || c.source}</div>
                </div>
                <PrivacyBadge status={c.privacy_status} />
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800">Preventivi in Attesa di Follow-up</h3>
            <div className="flex items-center gap-2">
              <PlusButton testid="add-quotes_awaiting" onClick={() => openAdd("quotes_awaiting", "Preventivi in Attesa di Follow-up")} />
              <button onClick={() => navigate("/preventivi")} className="text-xs text-primary font-medium hover:underline">Vedi tutti</button>
            </div>
          </div>
          <div className="space-y-2">
            {data.quotes_awaiting.length === 0 && manualFor("quotes_awaiting").length === 0 && <div className="text-sm text-muted-foreground py-4">Nessun preventivo in attesa.</div>}
            {renderManual("quotes_awaiting")}
            {data.quotes_awaiting.map((q) => (
              <div key={q.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{q.number} · {q.recipient}</div>
                  <div className="text-xs text-muted-foreground">{eur(q.total)}</div>
                </div>
                <QuoteStatusBadge status={q.status} />
              </div>
            ))}
          </div>
        </Card>
      </div>

      <EntryDialog state={dialog} onClose={closeDialog} onSaved={load} />
    </div>
  );
}

const SECTION_LABELS = {
  stalled_operations: "Operazioni ferme da tempo",
  overdue_followups: "Follow-up in Ritardo",
  upcoming_actions: "Prossime Azioni",
  new_contacts: "Nuovi Contatti",
  quotes_awaiting: "Preventivi in Attesa di Follow-up",
};
